import { ApolloLink } from "@apollo/client";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useMutation } from "@apollo/client/react";
import { ReactNode } from "react";
import { toast } from "react-toastify";

import { useNavigator, UseNavigatorType } from "../../lib/Hooks";
import { toLocalAndUtcDates } from "../../lib/Util";
import { CoreIcon } from "../common/StatusIcon";
import { HumanDate } from "../Date";

import {
  CircularProgress,
  Link,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { UserAvatarWithTooltip } from "../ui/UserAvatar";

import { FixedWithCol } from "../ui/muiTables";

import { ArchiveIcon } from "@phosphor-icons/react/dist/csr/Archive";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ClockCounterClockwise";
import { FileIcon } from "@phosphor-icons/react/dist/csr/File";
import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { StarIcon } from "@phosphor-icons/react/dist/csr/Star";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { Link as RouterLink } from "react-router-dom";
import Permissions from "../../constants/permissions";
import { useAccountContext } from "../../strict/app/AccountResolver";
import {
  BranchChangelistDocument,
  BranchChangelistMutation,
  BranchChangelistMutationVariables,
  SaveChangelistDocument,
  User,
} from "../../graphql/types";
import typography from "../../styles/sbDesignSystem/sbTypography";
import { IfPermission } from "../Permissions";
import { Badge } from "../ui/badges";
import { IconButton } from "../ui/icons";
import { StatefulButton } from "../ui/muiButtons";

const CoreIconStyled = styled(CoreIcon)`
  & {
    font-size: 1em;
  }
`;

const handleSaveDraft = ({
  browser,
  saveDraft,
  navigator,
  from_draft,
}: {
  browser: string;
  saveDraft: useMutation.MutationFunction<BranchChangelistMutation, BranchChangelistMutationVariables>;
  navigator: UseNavigatorType;
  from_draft?: DraftRef;
}) => {
  const payload: BranchChangelistMutationVariables = {
    description: `Version Created on ${browser}`,
  };
  const editorPath = "editor";
  // Are we creating a draft from a current version?
  if (from_draft) {
    payload["changelist_id"] = from_draft.id;
  }
  if (payload["description"].length >= 500) {
    payload["description"] = payload["description"].slice(0, 500);
  }
  saveDraft({ variables: payload })
    .then((result: ApolloLink.Result<BranchChangelistMutation>) => {
      const changelist = result?.data?.branch_changelist?.changelist;
      if (!changelist) {
        console.error("No changelist returned from branch_changelist mutation");
        return;
      }
      navigator.goto(
        ["scripts", editorPath, changelist?.id],
        {},
        {
          addReferrer: true,
        }
      );
      toast.success("Draft Created");
    })
    .catch(() => {
      toast.error("There was a problem creating your Draft.");
    });
};

const AddDraft = ({ from_draft }: { from_draft?: DraftRef }) => {
  const navigator = useNavigator();
  const { browser } = toLocalAndUtcDates(new Date().toISOString());
  const [saveDraft, { loading, error }] = useMutation<BranchChangelistMutation, BranchChangelistMutationVariables>(
    BranchChangelistDocument
  );
  let errorMessage = "There was a problem saving the draft.";

  if (CombinedGraphQLErrors.is(error)) {
    errorMessage = error.errors[0]?.message ?? errorMessage;
  } else if (error?.message) {
    errorMessage = error.message;
  }
  return (
    <IfPermission permission={Permissions.DevelopScripts}>
      {error ? (
        <Tooltip id="tooltip" title={`Clone Draft Error: ${errorMessage}`} placement="left" arrow>
          <div>
            <CoreIconStyled type={"error"} />
          </div>
        </Tooltip>
      ) : (
        <StatefulButton
          startIcon={<PlusIcon />}
          loading={loading}
          data-cy="create-draft-button"
          onClick={() => {
            handleSaveDraft({ browser, saveDraft, navigator, from_draft });
          }}
        >
          Draft
        </StatefulButton>
      )}
    </IfPermission>
  );
};

const AddDraftIcon = ({ from_draft }: { from_draft: DraftRef }) => {
  const navigator = useNavigator();
  const { browser } = toLocalAndUtcDates(new Date().toISOString());
  const [saveDraft, { loading, error }] = useMutation<BranchChangelistMutation, BranchChangelistMutationVariables>(
    BranchChangelistDocument
  );
  let errorMessage = "There was a problem saving the draft.";
  if (CombinedGraphQLErrors.is(error)) {
    errorMessage = error.errors[0]?.message ?? errorMessage;
  } else if (error?.message) {
    errorMessage = error.message;
  }
  return (
    <IfPermission permission={Permissions.DevelopScripts}>
      {error ? (
        <Tooltip id="tooltip" title={`Clone Draft Error: ${errorMessage}`} placement="left" arrow>
          <div>
            <CoreIconStyled type={"error"} />
          </div>
        </Tooltip>
      ) : (
        <Tooltip id="tooltip" title="Create Draft from this version." placement="left" arrow>
          <IconButton
            className="add-draft"
            data-cy="tooltip-container-add-draft"
            onClick={() => {
              if (loading) return;
              handleSaveDraft({ browser, saveDraft, navigator, from_draft });
            }}
          >
            {loading ? <CircularProgress size={20} /> : <PlusIcon />}
          </IconButton>
        </Tooltip>
      )}
    </IfPermission>
  );
};
const ArchiveDraft = ({ changelist_id }: { changelist_id: string }) => {
  const [archiveDraft, { loading, error }] = useMutation(SaveChangelistDocument);
  const navigator = useNavigator();
  const handleArchiveDraft = () => {
    const payload = {
      changelist_id: changelist_id,
      action: "archive",
    };
    archiveDraft({ variables: payload })
      .then(() => {
        navigator.goto(
          "scripts/drafts",
          {},
          {
            addReferrer: true,
          }
        );
        toast.success("Draft Archived");
      })
      .catch(() => {
        toast.error("There was a problem creating your Draft.");
      });
  };

  let errorMessage = "There was a problem archiving the draft.";
  if (CombinedGraphQLErrors.is(error)) {
    errorMessage = error.errors[0]?.message ?? errorMessage;
  } else if (error?.message) {
    errorMessage = error.message;
  }
  return (
    <IfPermission permission={Permissions.DevelopScripts}>
      <Tooltip id="tooltip" title="Archive Draft" placement="left" arrow>
        {error ? (
          <Tooltip id="tooltip" title={`Archive Draft Error: ${errorMessage}`} placement="left" arrow>
            <div>
              <CoreIconStyled type={"error"} />
            </div>
          </Tooltip>
        ) : (
          <IconButton className="archive-draft" data-cy="archive-draft" onClick={handleArchiveDraft}>
            {loading ? <CircularProgress size={20} /> : <ArchiveIcon />}
          </IconButton>
        )}
      </Tooltip>
    </IfPermission>
  );
};

interface TableWithPaginationProps {
  show: boolean;
}

const TableWithPagination = styled("div")<TableWithPaginationProps>`
  padding-top: ${(props) => (props.show ? "50px" : "initial")};
  position: relative;
`;

const SmallLeftMargin = styled("div")`
  display: inline-block;
  margin-left: 5px;
`;

const IconDraftContainer = styled("div")`
  display: inline-block;
`;

const DraftIcon = ({ status }: { status?: string | null }) => {
  const theme = useTheme();
  let tooltipTitle = "Active Draft";
  const commonProps = {
    className: "draft-status",
    size: "medium",
  };
  let icon = <FileIcon {...commonProps} size="28px" />;

  if (status === "running") {
    tooltipTitle = "Published";
    icon = <StarIcon {...commonProps} size="28px" color={theme.palette.accentWarning30.main} />;
  } else if (status === "replaced") {
    tooltipTitle = "Previously Published";
    icon = <ClockCounterClockwiseIcon {...commonProps} size="28px " color={theme.palette.text30.main} />;
  }

  return (
    <Tooltip id="tooltip" title={tooltipTitle} placement="right" arrow>
      <IconDraftContainer>{icon}</IconDraftContainer>
    </Tooltip>
  );
};

const DraftLink = ({ description, id }: { description: string; id: string }) => {
  const { urlName } = useAccountContext();

  return (
    <Tooltip title={<Typography sx={{ maxWidth: 300 }}>{description}</Typography>}>
      <Link
        component={RouterLink}
        to={`/app/${urlName}/scripts/editor/${id}`}
        data-testid="draft-name"
        sx={(theme) => ({
          ...(typography && typography["body.small.link"]),
          color: theme.palette.text50.main,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 500,
        })}
      >
        {description}
      </Link>
    </Tooltip>
  );
};

const DraftLinkStyled = ({ description, id }: { description: string; id: string }) => {
  const nav = useNavigator();
  return (
    <Typography
      sx={{
        "&:hover": {
          cursor: "pointer",
        },
      }}
      variant="body.small.link"
      className={"draft-name"}
      data-cy="draft-name"
      onClick={() => {
        nav.goto(
          ["scripts", "editor", id],
          {},
          {
            addReferrer: true,
          }
        );
      }}
    >
      {description}
    </Typography>
  );
};

const DraftsLink = ({ description = "drafts" }: { description?: string }) => {
  const nav = useNavigator();
  return (
    <Typography
      variant="body.small.regular"
      onClick={() => {
        nav.goto(
          "scripts/drafts",
          {},
          {
            addReferrer: true,
          }
        );
      }}
    >
      {description}
    </Typography>
  );
};

const NeedsMerge = ({ required }: { required?: boolean | null }) => {
  if (required) {
    const msg = `This draft was branched from an older version than the current Published version.  Its contents maybe out of date.`;

    return (
      <Tooltip id="tooltip" title={msg} placement="right" arrow>
        <Badge icon={<WarningIcon />} label="Needs Merge" severity="warning" size="small" />
      </Tooltip>
    );
  } else {
    return null;
  }
};

interface DraftRef {
  id: string;
}

interface HistoryDraft extends DraftRef {
  description: string;
  status: string;
  created_by: Pick<User, "name" | "email">;
  created_on: string;
  published_by?: Pick<User, "name" | "email"> | null;
  published_on?: string | null;
}

const HistoryRow = ({ draft }: { draft: HistoryDraft }) => (
  <TableRow className={"history-row"}>
    <TableCell>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <DraftIcon status={draft.status} />
        <DraftLinkStyled id={draft.id} description={draft.description} />
        <UserAvatarWithTooltip user={draft.created_by} prefix={"Created by "} userDate={draft.created_on} />
      </Stack>
    </TableCell>
    <TableCell className="draft-published">
      <HumanDate date={draft.published_on} />
      <SmallLeftMargin>
        {draft.published_by ? (
          <UserAvatarWithTooltip
            user={draft.published_by}
            prefix={"Published by "}
            userDate={draft.published_on ?? ""}
          />
        ) : null}
      </SmallLeftMargin>
    </TableCell>
    <IfPermission permission={Permissions.DevelopScripts}>
      <FixedWithCol>
        {draft.status === "running" ? <AddDraft from_draft={draft} /> : <AddDraftIcon from_draft={draft} />}
      </FixedWithCol>
    </IfPermission>
  </TableRow>
);

const NoPublishedRow = () => (
  <TableRow>
    <TableCell>No Published Drafts</TableCell>
    <TableCell className="draft-updated"></TableCell>
    <FixedWithCol>
      <AddDraft />
    </FixedWithCol>
  </TableRow>
);

const HistoryTable = ({ drafts }: { drafts?: HistoryDraft[] | null }) => {
  let draftRows: ReactNode[] = [];
  if ((drafts || []).length === 0) {
    draftRows.push(<NoPublishedRow key={0} />);
  } else {
    draftRows = (drafts ?? []).map((draft, index) => {
      return <HistoryRow key={index} draft={draft} />;
    });
  }
  return (
    <TableContainer component={Paper}>
      <Table data-cy="history-table" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell width={"200px"}>Published</TableCell>
            <IfPermission permission={Permissions.DevelopScripts}>
              <TableCell>Options</TableCell>
            </IfPermission>
          </TableRow>
        </TableHead>
        <TableBody>{draftRows}</TableBody>
      </Table>
    </TableContainer>
  );
};

export {
  AddDraft,
  AddDraftIcon,
  ArchiveDraft,
  DraftIcon,
  DraftLink,
  DraftLinkStyled,
  DraftsLink,
  FixedWithCol,
  HistoryTable,
  NeedsMerge,
  SmallLeftMargin,
  TableWithPagination,
};
export type { HistoryDraft };
